import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { LocalStorageKey } from '@constants/local-storage.constants';
import { PermissionHelper } from '@core/helpers/permission.helper';
import { User } from '@core/typings/dc-classes';
import { Permissions, Roles } from '@core/typings/dc-enums';
import { catchError, from, Observable, of, switchMap } from 'rxjs';

import { FetcherService } from './fetcher.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private fetcherService: FetcherService = inject(FetcherService);

    public user: WritableSignal<User | null> = signal(null);

    private authenticated = false;
    private isSigningOut = false;

    public checkAuthentication(): Observable<boolean> {
        if (this.authenticated) {
            return of(true);
        }

        return this.loadAuthentication();
    }

    public saveAuthentication(user: User, token: string): void {
        if (token) {
            this.setAccessToken(token);
        }

        this.fetcherService.initFetcher(token);

        this.setUser(user);

        this.authenticated = true;
    }

    public setUser(user: User | null): void {
        this.user.set(user);
    }

    public logOut(): Promise<void> {
        return new Promise((resolve) => {
            if (this.isSigningOut) {
                return;
            }

            this.isSigningOut = true;

            this.fetcherService.fetcher
                .path('/logout')
                .method('post')
                .create()({})
                .catch(console.error)
                .finally(() => {
                    localStorage.removeItem(LocalStorageKey.accessToken);

                    this.setUser(null);
                    this.setAccessToken(null);
                    this.fetcherService.initFetcher(null);

                    this.authenticated = false;
                    this.isSigningOut = false;
                    resolve();
                });
        });
    }

    public hasRole(role: Roles): boolean {
        return PermissionHelper.hasRole(this.user()!, role);
    }

    public hasPermission(permission: Permissions, checkRolesForPermissions: boolean): boolean {
        return PermissionHelper.hasPermission(this.user()!, permission, checkRolesForPermissions);
    }

    public hasPermissionTo(permission: Permissions): boolean {
        return this.hasPermission(permission, true);
    }

    private setAccessToken(accessToken: string | null): void {
        if (accessToken) {
            localStorage.setItem(LocalStorageKey.accessToken, accessToken);
        } else {
            localStorage.removeItem(LocalStorageKey.accessToken);
        }
    }

    private loadAuthentication(): Observable<boolean> {
        const accessToken = localStorage.getItem(LocalStorageKey.accessToken);
        if (!accessToken) {
            return of(false);
        }

        this.fetcherService.initFetcher(accessToken);

        return from(this.fetcherService.fetcher.path('/user').method('get').create()({})).pipe(
            switchMap((res) => {
                if (!PermissionHelper.hasPermission(res.data, 'access gds', true)) {
                    this.logOut();
                    return of(false);
                }

                this.saveAuthentication(res.data, accessToken);
                return of(true);
            }),
            catchError((err) => {
                if (typeof err.getActualType === 'function' && err.getActualType() !== undefined) {
                    const error = err.getActualType();
                    if (error.status === 401) {
                        this.logOut();
                    }
                }
                return of(false);
            })
        );
    }
}
